import React, { useMemo, forwardRef } from 'lib/react';
import clsx from 'lib/clsx';
import {
  EVAvatar,
  EVButton,
  EVKeyboardArrowUp,
  EVKeyboardArrowDown,
  EVTypography,
  makeStyles,
  EVGrid,
} from 'lib/components';
import { getUserDetails } from 'utils/app.utils';
import PropTypes from 'prop-types';

const useStyles = makeStyles((theme) => ({
  userProfileButton: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '0px',
    padding: '0px 8px',
    '@media (max-width: 1024px)': {
      padding: '0px',
      width: '100%',
    },
    '&.open': {
      backgroundColor: theme.evPalette.ocean[600],
    },
  },
  avatarMedium: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'normal',
    width: theme.spacing(5.5),
    height: theme.spacing(5.5),
    backgroundColor: theme.evPalette.ocean[600],
  },
  avatarOpen: {
    color: theme.evPalette.ocean[600],
    backgroundColor: 'white',
  },
  keyboardArrow: {
    margin: `${theme.spacing(1.5)}px ${theme.spacing(0.5)}px`,
    '&.up': { color: 'white' },
    '&.down': { color: theme.evPalette.ocean[600] },
  },
  keyboardArrowUpOpen: {
    color: 'white',
  },
  name: {
    textTransform: 'capitalize',
    color: theme.evPalette.charcoal[900],
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(22),
    fontWeight: '500',
    textAlign: 'left',
  },
  avatarWithText: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
}));

function UserProfileButton(props, avatarRef) {
  const {
    displayName, handleClick, isMenuOpen,
  } = props;
  const classes = useStyles();

  const userDetails = useMemo(() => getUserDetails(), []);
  const { firstName = '', lastName = '' } = userDetails;
  const initials = (firstName.charAt(0).toUpperCase()) + (lastName.charAt(0).toUpperCase());

  return (
    <EVButton
      id="user-menu-button"
      className={clsx(classes.userProfileButton, isMenuOpen && 'open')}
      onClick={handleClick}
      aria-expanded={isMenuOpen}
      aria-controls="user-menu"
      ref={avatarRef}
    >
      <EVGrid className={classes.avatarWithText}>
        <EVAvatar
          id="userAvatar"
          className={clsx(classes.avatarMedium, isMenuOpen && classes.avatarOpen)}
        >
          {initials}
        </EVAvatar>
        { displayName && <EVTypography id="fullName" className={classes.name}>{`${firstName} ${lastName}`}</EVTypography>}
      </EVGrid>
      { isMenuOpen
        ? <EVKeyboardArrowUp className={clsx(classes.keyboardArrow, 'up')} />
        : <EVKeyboardArrowDown className={clsx(classes.keyboardArrow, 'down')} />}
    </EVButton>
  );
}

UserProfileButton.defaultProps = {
  displayName: false,
  isMenuOpen: false,
};

UserProfileButton.propTypes = {
  displayName: PropTypes.bool,
  handleClick: PropTypes.func.isRequired,
  isMenuOpen: PropTypes.bool,
};

export default forwardRef(UserProfileButton);
